import { create } from 'zustand'

export type ToastVariant = 'error' | 'success' | 'warning' | 'info'

/** A single button on a toast — "Undo", "View", … Pressing it dismisses the toast. */
export interface ToastAction {
  label: string
  onClick: () => void
}

export interface Toast {
  id: string
  type: ToastVariant
  title: string
  description?: string
  action?: ToastAction
}

export interface AddToastInput {
  type?: ToastVariant
  title: string
  description?: string
  action?: ToastAction
}

/** Options for the `toast.*` helpers beyond title and description (CLA-1257). */
export interface ToastOptions {
  action?: ToastAction
}

const MAX_VISIBLE = 5
const DURATION_DEFAULT = 5000
const DURATION_ERROR = 8000
// A toast with an action has to stay long enough to reach for the button.
const DURATION_ACTION = 8000

interface ToastState {
  toasts: Toast[]
  addToast: (input: AddToastInput) => string
  removeToast: (id: string) => void
}

let counter = 0
const timers = new Map<string, ReturnType<typeof setTimeout>>()

export const useToastStore = create<ToastState>((set) => {
  const remove = (id: string) => {
    const timer = timers.get(id)
    if (timer) clearTimeout(timer)
    timers.delete(id)
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
  }

  return {
    toasts: [],
    addToast: (input) => {
      const id = `toast-${++counter}`
      const type = input.type ?? 'info'
      const toast: Toast = { id, type, title: input.title, description: input.description, action: input.action }
      const duration = input.action ? DURATION_ACTION : type === 'error' ? DURATION_ERROR : DURATION_DEFAULT

      set((state) => {
        const next = [...state.toasts, toast]
        // Evict oldest if exceeding max visible
        return { toasts: next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next }
      })

      timers.set(id, setTimeout(() => remove(id), duration))
      return id
    },
    removeToast: remove,
  }
})

/** Convenience function for use outside React components (e.g. React Query config) */
export const toast = {
  success: (title: string, description?: string, opts?: ToastOptions) =>
    useToastStore.getState().addToast({ type: 'success', title, description, action: opts?.action }),
  error: (title: string, description?: string, opts?: ToastOptions) =>
    useToastStore.getState().addToast({ type: 'error', title, description, action: opts?.action }),
  warning: (title: string, description?: string, opts?: ToastOptions) =>
    useToastStore.getState().addToast({ type: 'warning', title, description, action: opts?.action }),
  info: (title: string, description?: string, opts?: ToastOptions) =>
    useToastStore.getState().addToast({ type: 'info', title, description, action: opts?.action }),
}
